import {
  BusinessImprovementsDto,
  GetBusinessInfoResponse,
  MyBusiness,
} from "@1games-ru/region-shared/dist/backend/requests/business";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import formatPrice from "@/core/helpers/formatPrice";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { ArrowsUpIcon, Lock3Icon } from "@/shared/icons";
import { Button, Icon, TabsNavigation } from "@/shared/ui";
import DeviceTabContentWrapper from "@/shared/widgets/DeviceTabContentWrapper";
import HeaderTitle from "@/shared/widgets/HeaderTitle";
import ImprovementInfoWrapper from "@/shared/widgets/ImprovementInfoWrapper";
import ImprovementsItem from "@/shared/widgets/ImprovementsItem";
import { ImprovementsTab } from "@/domains/business/constants/improvementsTab";
import {
  improvementsCurrentTabSelector,
  improvementsListSelector,
  improvementsTabsSelector,
  selectedImprovementSelector,
  setSelectedImprovement,
  setSelectedImprovementsTab,
} from "@/domains/business/slice";
import { improvementsIconsList } from "@/domains/business/helpers/improvementsIconsList";
import useImprovements from "@/domains/business/hooks/useImprovements";

type Props = {
  info: MyBusiness | GetBusinessInfoResponse;
  onClose: () => void;
};

export default function BusinessImprovementsContainer({ info, onClose }: Props) {
  const { t } = useTranslation("business");
  const dispatch = useAppDispatch();
  const improvementsList = useAppSelector(improvementsListSelector);
  const improvementsTabs = useAppSelector(improvementsTabsSelector);
  const improvementsCurrentTab = useAppSelector(improvementsCurrentTabSelector);
  const selectedImprovement = useAppSelector(selectedImprovementSelector);

  const { isLoading, buyImprovement, isBuying } = useImprovements(info?.id);

  const currentLevel = selectedImprovement ? selectedImprovement.currentLevel : 1;
  const description = selectedImprovement
    ? t(`business.improvements.${selectedImprovement.type.toLowerCase()}.description`)
    : "-";

  let improvementType = "-";

  if (selectedImprovement) {
    improvementType = selectedImprovement.isUnique
      ? t("business.improvements.unique")
      : t("business.improvements.standard");
  }

  const improvementIcon =
    improvementsIconsList[selectedImprovement?.type?.toLowerCase()] || ArrowsUpIcon;

  const renderLevel = (level, improvement) => {
    const IconName =
      level.isBought || level.level === 1
        ? improvementsIconsList[improvement.type.toLowerCase()]
        : Lock3Icon;

    return (
      <div
        key={level.id}
        className="flex items-center gap-2.5 py-3.5 px-1 border-b border-b-[#191919]"
      >
        <IconName
          className={clsx("size-4.5", improvement.currentLevel < level.level && "text-[#434242]")}
        />
        <p className="min-w-[4.75rem] text-16 font-medium">Уровень {level.level}</p>
        <p className="py-1 px-1.5 bg-accent-solid/20 text-12 font-bold text-accent-solid rounded-[0.3125rem]">
          {level.profits[0].value > 0 && "+"}
          {level.profits[0].value}%
        </p>
        <p className="ml-auto text-16 font-bold text-accent-solid">{formatPrice(level.price)} ₽</p>
      </div>
    );
  };

  const getImprovementData = (improvement: BusinessImprovementsDto) => {
    const { levels } = improvement;

    return {
      maxPrice: Math.max(...levels.map((level) => level.price)),
      activeProfit: levels
        .filter((level) => level.isBought)
        .reduce((max, level) => Math.max(max, level?.profits[0]?.value), 0),
      levelsJsx: levels.map((level) => renderLevel(level, improvement)),
    };
  };

  const improvementData = selectedImprovement
    ? getImprovementData(selectedImprovement)
    : { maxPrice: 0, activeProfit: 0, levelsJsx: [] };

  const { maxPrice, activeProfit, levelsJsx } = improvementData;

  const uniqueImprovements = improvementsList?.filter((item) => item.isUnique) || [];
  const standardImprovements = improvementsList?.filter((item) => !item.isUnique) || [];

  const changeCurrentTab = useCallback(
    (tab: ImprovementsTab) => dispatch(setSelectedImprovementsTab(tab)),
    [dispatch],
  );

  const onBack = useCallback(() => {
    dispatch(setSelectedImprovement(null));
  }, [dispatch]);

  const handleClick = useCallback(
    (item: BusinessImprovementsDto) => {
      dispatch(setSelectedImprovement(item));
    },
    [dispatch],
  );

  const headerTitle = selectedImprovement
    ? t(`business.improvements.list.${selectedImprovement.type.toLowerCase()}`)
    : "";

  const header = (
    <HeaderTitle
      title={t("business.improvements.title")}
      isOpen={!!selectedImprovement}
      titleOpen={headerTitle}
      onBack={onBack}
    />
  );

  const footer =
    selectedImprovement?.currentLevel < selectedImprovement?.maxLevel ? (
      <div className="flex flex-col gap-5">
        <div className="flex justify-between items-end">
          <div className="flex flex-col gap-1.5">
            <p className="text-20 font-bold text-accent-solid">{formatPrice(maxPrice)} ₽</p>
            <p className="text-15 font-medium text-white/50">{t("business.improvements.cost")}</p>
          </div>
          <p className="text-15 font-medium text-white/20">
            {t("business.improvements.activateDescription")}
          </p>
        </div>
        <Button
          className="gap-2.5 h-11"
          onClick={() => selectedImprovement && buyImprovement(selectedImprovement.id)}
          loading={isBuying}
          disabled={isBuying || !selectedImprovement}
        >
          <Icon name="card" className="w-4.5 h-3.5" />
          {t("business.improvements.buyImprovement")}
        </Button>
      </div>
    ) : null;

  useEffect(() => {
    return () => {
      dispatch(setSelectedImprovement(null));
    };
  }, [dispatch]);

  return (
    <DeviceTabContentWrapper
      header={header}
      footer={!!selectedImprovement && footer}
      onClose={onClose}
    >
      {!selectedImprovement ? (
        <>
          {isLoading && (
            <div className="absolute z-10 top-1/2 left-1/2 flex items-center justify-center p-4 -translate-x-1/2 -translate-y-1/2">
              <div className="mz-loader" />
            </div>
          )}
          {/* TODO Раскомментировать когда добавят уникальные, поправить при необходимости ||||| */}
          {/* <TabsNavigation
            tabs={improvementsTabs}
            selectedTab={improvementsCurrentTab}
            i18fileName="business"
            i18Path="business.improvements.tabs"
            onClick={changeCurrentTab}
            className="mb-2.5"
          /> */}
          {(improvementsCurrentTab === ImprovementsTab.All ||
            improvementsCurrentTab === ImprovementsTab.Standard) && (
            <div className="grid grid-cols-3 gap-1.5 mb-5">
              {standardImprovements?.length > 0 &&
                standardImprovements.map((item) => (
                  <ImprovementsItem
                    key={item.id}
                    icon={improvementsIconsList[item?.type.toLowerCase()] || ArrowsUpIcon}
                    iconName={item?.type.toLowerCase()}
                    levelFrom={item.currentLevel}
                    levelTo={item.maxLevel}
                    onClick={() => handleClick(item)}
                    status={item.isBought}
                  />
                ))}
            </div>
          )}
          {/* TODO Раскомментировать когда добавят уникальные, поправить при необходимости ||||| */}
          {/* {improvementsCurrentTab === ImprovementsTab.All && uniqueImprovements?.length > 0 && (
            <p className="mb-3.5 text-12 font-bold uppercase leading-none">
              {t("business.improvements.uniqueImprovements")}:
            </p>
          )}
          {(improvementsCurrentTab === ImprovementsTab.All ||
            improvementsCurrentTab === ImprovementsTab.Unique) && (
            <div className="grid grid-cols-4 gap-1.5">
              {uniqueImprovements?.length > 0 &&
                uniqueImprovements.map((item) => (
                  <ImprovementsItem
                    key={item.id}
                    icon={improvementsIconsList[item?.type.toLowerCase()] || ArrowsUpIcon}
                    iconName={item?.type.toLowerCase()}
                    levelFrom={item.currentLevel}
                    levelTo={item.maxLevel}
                    onClick={handleClick}
                    status={item.isBought}
                  />
                ))}
            </div>
          )} */}
        </>
      ) : (
        <ImprovementInfoWrapper description={description} icon={improvementIcon}>
          <div className="pb-5">
            <div className="flex justify-between items-center py-5 px-1 border-b border-b-[#191919]">
              <p className="text-16 font-medium text-white/50">
                {t("business.improvements.improvementType")}:
              </p>
              <p className="text-16 font-medium text-white">{improvementType}</p>
            </div>
            <div className="flex justify-between items-center py-5 px-1 border-b border-b-[#191919]">
              <p className="text-16 font-medium text-white/50">
                {t("business.improvements.currentLevel")}:
              </p>
              <p className="text-16 font-medium text-white">{currentLevel}</p>
            </div>
            <div className="flex justify-between items-center py-5 px-1 border-b border-b-[#191919]">
              <p className="text-16 font-medium text-white/50">
                {t("business.improvements.percent")}:
              </p>
              <p className="text-16 font-bold text-accent-solid">{activeProfit}%</p>
            </div>
          </div>

          {levelsJsx?.length > 0 && (
            <div>
              <p className="mb-1.5 text-12 font-bold uppercase">
                {t("business.improvements.improvementLevels")}:
              </p>
              {levelsJsx}
            </div>
          )}
        </ImprovementInfoWrapper>
      )}
    </DeviceTabContentWrapper>
  );
}
